/**
 * Bench Test Service (transplanted from onboarding app)
 *
 * START/STOP triggers + telemetry polling for screening workflow.
 */

const BENCH_TEST_API_URL = 'https://bsckgrt140.execute-api.us-east-1.amazonaws.com/prod/bench-test';
const BENCH_JOBS_API_URL = 'https://1tokl8cb48.execute-api.us-east-1.amazonaws.com/prod/bench-jobs';

// Physical bench sensors used for field screening. These are the REAL MACs the sensors
// report over RF; enrichment resolves identity by these. Canonical mount mapping
// (see onboarding lambda/sensor-health-check): a9:07 = Drive End, a4:a6 = Non-Drive End.
// Registering an active capture for these MACs makes enrichment stamp a screening-scoped
// identity (motorId=screening_<captureId>, dataClass=screening) for the duration, so the
// readings show up in the live poll AND never attach to the motors these bench sensors
// are normally installed on (Old/New Air Compressor).
export const BENCH_SENSOR_MACS = [
  '00:13:a2:00:42:35:a9:07', // DE  → BENCH-DE
  '00:13:a2:00:42:35:a4:a6', // NDE → BENCH-NDE
];

/**
 * Fire START to bench-test API for both sensors.
 * Fire-and-forget — logs errors but doesn't throw.
 */
export async function triggerBenchStart(captureId, motorInfo = {}) {
  // 1) Register the active screening capture FIRST so enrichment is already
  //    routing a9:07/a4:a6 to screening_<captureId> before any reading arrives.
  //    (Non-blocking: if this fails, the STOP-side clear and the 30-min TTL still
  //    protect production data; the panels just won't populate.)
  await registerScreeningCapture(captureId, motorInfo.facilityName || '');

  // 2) Notify Node-RED that a bench/screening capture is active (metadata only —
  //    bench-test Lambda is locked; it never sends sensor config commands).
  const payload = {
    command: 'START',
    captureId,
    sensorDEMac: 'BENCH-DE',
    sensorNDEMac: 'BENCH-NDE',
    captureType: 'SCREENING',
    sourceCustomer: motorInfo.facilityName || '',
    motorHP: motorInfo.hp || null,
    motorRPM: motorInfo.rpm || null,
    timestamp: new Date().toISOString(),
  };
  try {
    const res = await fetch(BENCH_TEST_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    console.log('Screening START:', data);
    return data;
  } catch (err) {
    console.error('Screening START failed (non-blocking):', err);
  }
}

/**
 * Register a live screening capture so enrichment routes the bench sensors to a
 * screening-scoped identity for the duration. Fire-and-forget.
 */
export async function registerScreeningCapture(captureId, facilityName = '') {
  try {
    const res = await fetch(`${BENCH_JOBS_API_URL}/active-capture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ captureId, sensorMacs: BENCH_SENSOR_MACS, facilityName }),
    });
    const data = await res.json();
    console.log('Active capture registered:', data);
    return data;
  } catch (err) {
    console.error('Active-capture register failed (non-blocking):', err);
  }
}

/**
 * Clear the live screening capture (STOP). Enrichment reverts the bench sensors to
 * their registry identity. Fire-and-forget; a 30-min TTL is the backstop.
 */
export async function clearScreeningCapture() {
  try {
    const res = await fetch(`${BENCH_JOBS_API_URL}/active-capture`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sensorMacs: BENCH_SENSOR_MACS }),
    });
    const data = await res.json();
    console.log('Active capture cleared:', data);
    return data;
  } catch (err) {
    console.error('Active-capture clear failed (non-blocking):', err);
  }
}

/**
 * Fire STOP to bench-test API for both sensors.
 */
export async function triggerBenchStop(captureId) {
  // Clear the active-capture registration first so enrichment stops routing the
  // bench sensors to this screening id (reverts them to registry identity).
  await clearScreeningCapture();

  const payload = {
    command: 'STOP',
    captureId,
    sensorDEMac: 'BENCH-DE',
    sensorNDEMac: 'BENCH-NDE',
    timestamp: new Date().toISOString(),
  };
  try {
    const res = await fetch(BENCH_TEST_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    console.log('Screening STOP:', data);
    return data;
  } catch (err) {
    console.error('Screening STOP failed (non-blocking):', err);
  }
}

/**
 * Poll bench sensor telemetry from the bench-jobs API.
 * Returns { sensors: { 'BENCH-DE': { latest, count }, 'BENCH-NDE': { latest, count } } }
 */
export async function getBenchTelemetry(since, captureId) {
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  if (captureId) params.set('captureId', captureId);
  const url = `${BENCH_JOBS_API_URL}/telemetry?${params.toString()}`;
  const response = await fetch(url);
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || `API error: ${response.status}`);
  return result;
}
